import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import EnvContext from "@/core/env/env-context";
import { formatDocumentTitle } from "@/core/env/use-document-title";

export interface Crumb {
  label: string;
  href?: string;
  state?: unknown;
}

interface BreadcrumbsContextValue {
  pageCrumbs: Crumb[] | null;
  /** The bar. A page can register crumbs for the tab title and hide the bar. */
  crumbsVisible: boolean;
  setPageCrumbs: (crumbs: Crumb[] | null) => void;
  setCrumbsVisible: (visible: boolean) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextValue>({
  pageCrumbs: null,
  crumbsVisible: true,
  setPageCrumbs: () => {},
  setCrumbsVisible: () => {},
});

const pageLabel = (crumbs: Crumb[] | null, fallback?: string) => {
  const leaf = crumbs?.at(-1)?.label;
  const root = crumbs?.[0]?.label;
  if (leaf && root && leaf !== root) return `${leaf} · ${root}`;
  return leaf ?? fallback;
};

export const BreadcrumbsProvider = ({
  fallback,
  children,
}: {
  fallback?: string;
  children: ReactNode;
}) => {
  const [pageCrumbs, setPageCrumbs] = useState<Crumb[] | null>(null);
  const [crumbsVisible, setCrumbsVisible] = useState(true);
  const appEnv = useContext(EnvContext)?.appEnv;
  const value = useMemo(
    () => ({ pageCrumbs, crumbsVisible, setPageCrumbs, setCrumbsVisible }),
    [crumbsVisible, pageCrumbs],
  );

  useEffect(() => {
    if (!appEnv) return;
    document.title = formatDocumentTitle(
      pageLabel(pageCrumbs, fallback),
      appEnv,
    );
  }, [appEnv, fallback, pageCrumbs]);

  return (
    <BreadcrumbsContext.Provider value={value}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const usePageCrumbs = () => useContext(BreadcrumbsContext).pageCrumbs;

export const useCrumbsVisible = () =>
  useContext(BreadcrumbsContext).crumbsVisible;

/** `visible: false` keeps the tab title and hides the bar. */
export const useBreadcrumbs = (
  crumbs: Crumb[] | null,
  options?: { visible?: boolean },
) => {
  const { setPageCrumbs, setCrumbsVisible } = useContext(BreadcrumbsContext);
  const visible = options?.visible ?? true;
  const key = JSON.stringify(crumbs);

  useEffect(() => {
    setPageCrumbs(key === "null" ? null : (JSON.parse(key) as Crumb[]));
    setCrumbsVisible(visible);
    return () => {
      setPageCrumbs(null);
      setCrumbsVisible(true);
    };
  }, [key, setCrumbsVisible, setPageCrumbs, visible]);
};
